import { motion } from "framer-motion";
import { Instagram, ArrowUpRight } from "lucide-react";

const CLIENTS = [
  {
    id: "c1",
    serif: "Kuldeep & Vanshika",
    script: "Kuldeep",
    role: "Indian Cricketer · Team India",
    handles: [
      { handle: "kuldeep_18", url: "https://www.instagram.com/kuldeep_18/" },
    ],
    heritage:
      "India's most-loved left-arm spinner chose us for a moment bigger than any final. We crafted a quiet, cinematic suite — a save-the-date film, a personalised e-invite with live countdown and a family of function cards — each carrying the same calm confidence he brings to the crease.",
    quote: "Traditional ho, but stadium jaisa bold lage.",
    cover:
      "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/wssipa5q_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg",
  },
  {
    id: "c2",
    serif: "Devanshi & Deep",
    script: "Together",
    role: "Lifestyle & Travel Creators",
    handles: [
      {
        handle: "devanshi_and_deep",
        url: "https://www.instagram.com/devanshi_and_deep/",
      },
    ],
    heritage:
      "Millions watch their life unfold on a feed — but the invite to the day itself had to feel private, precious, forever. We designed an unhurried film of typography, pastel frames and whispered music — the only post their audience didn't get to see first.",
    quote: "Rooted in heritage, alive in motion.",
    cover:
      "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/ijwqjd1e_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg",
  },
  {
    id: "c3",
    serif: "Nishika & RC",
    script: "Nishika",
    role: "Fashion, Travel & Fitness Creators",
    handles: [
      {
        handle: "nishika_Mehta1",
        url: "https://www.instagram.com/nishika_Mehta1/",
      },
      {
        handle: "Trainwithrc",
        url: "https://www.instagram.com/Trainwithrc/",
      },
    ],
    heritage:
      "Two creators known for strength and silhouette. For their wedding we dialled everything back — ivory paper, gold foil, a single heirloom motif. Delivered in 48 hours. Remembered for a lifetime.",
    quote: "An heirloom, rewritten in light.",
    cover:
      "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/mnpfelf3_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg",
  },
];

export default function CoupleStories() {
  return (
    <section
      id="stories"
      data-testid="couple-stories"
      className="relative py-28 md:py-40 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #141012 0%, #1b1618 100%)",
      }}
    >
      {/* Minimal ambient glows */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 20% 10%, rgba(232,165,180,0.08) 0%, transparent 40%)," +
            "radial-gradient(ellipse at 80% 90%, rgba(212,175,55,0.08) 0%, transparent 40%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Editorial header */}
        <div className="grid md:grid-cols-12 gap-8 mb-20 md:mb-28">
          <div className="md:col-span-5">
            <div className="overline text-[var(--iw-gold)] mb-8 tracking-[0.4em]">
              The Archive · Chapter III
            </div>
            <h2
              className="font-serif-display text-[var(--iw-ivory)] text-[44px] md:text-[72px] leading-[0.92] tracking-[-0.015em]"
              style={{ fontFamily: "Cormorant Garamond, serif" }}
            >
              Rooted
              <br />
              <span className="italic text-[var(--iw-ivory-dim)]">in</span>{" "}
              tradition.
              <br />
              <span className="italic gold-rose-gradient">Reimagined</span>
              <br />
              in design.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex md:items-end">
            <p className="text-[var(--iw-ivory-dim)] text-base md:text-lg leading-[1.7] max-w-md font-light">
              Three faces, three heirlooms, three quiet reinventions. Each
              invite began with a heritage detail the family didn't want to
              lose — and ended as a moving story on every guest's screen.
            </p>
          </div>
        </div>

        {/* Editorial stories */}
        <div className="space-y-28 md:space-y-40">
          {CLIENTS.map((c, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <motion.article
                key={c.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                className="grid md:grid-cols-12 gap-8 md:gap-14 items-center"
                data-testid={`client-card-${c.id}`}
              >
                {/* Image — taller, cinematic */}
                <a
                  href={c.handles[0].url}
                  target="_blank"
                  rel="noreferrer"
                  className={`relative group md:col-span-7 block ${
                    reverse ? "md:order-2 md:col-start-6" : ""
                  }`}
                >
                  <div className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden">
                    {/* Image */}
                    <img
                      src={c.cover}
                      alt={`${c.serif} — premium digital wedding invitation designed by InvitationWala`}
                      className="absolute inset-0 w-full h-full object-cover transition-all duration-[1600ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                      style={{
                        filter: "brightness(0.85) contrast(1.05) saturate(0.85)",
                      }}
                      draggable="false"
                      loading="lazy"
                      decoding="async"
                    />
                    {/* Subtle gold vignette frame */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-[8px] pointer-events-none"
                      style={{
                        border: "1px solid rgba(212,175,55,0.25)",
                      }}
                    />
                    {/* Dark gradient for legibility */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(180deg, rgba(10,10,10,0) 60%, rgba(10,10,10,0.75) 100%)",
                      }}
                    />
                    {/* Chapter number */}
                    <div
                      className="absolute top-6 left-6 font-serif-display italic text-[var(--iw-gold)] leading-none tracking-tight"
                      style={{
                        fontFamily: "Cormorant Garamond, serif",
                        fontSize: 42,
                      }}
                    >
                      /0{idx + 1}
                    </div>
                    {/* Script signature bottom */}
                    <div
                      className="absolute bottom-6 left-6 right-6 font-script text-[var(--iw-rose)] text-5xl md:text-6xl leading-none opacity-90"
                      style={{
                        fontFamily: "Tangerine, cursive",
                        fontWeight: 700,
                      }}
                    >
                      {c.script}
                    </div>
                    {/* Hover arrow */}
                    <span className="absolute top-6 right-6 w-11 h-11 grid place-items-center border border-[var(--iw-gold)]/40 text-[var(--iw-gold)] opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                </a>

                {/* Copy column */}
                <div
                  className={`md:col-span-5 ${
                    reverse ? "md:order-1 md:col-start-1 md:row-start-1" : "md:col-start-8"
                  }`}
                >
                  <div className="overline text-[var(--iw-gold)] mb-6">
                    / chapter {String(idx + 1).padStart(2, "0")}
                  </div>
                  <h3
                    className="font-serif-display text-[var(--iw-ivory)] text-4xl md:text-5xl leading-[1.02] tracking-tight"
                    style={{ fontFamily: "Cormorant Garamond, serif" }}
                  >
                    {c.serif}
                  </h3>
                  <div className="overline text-[var(--iw-faint)] mt-3">
                    {c.role}
                  </div>

                  {/* Thin hairline */}
                  <div
                    className="my-8 h-px w-16"
                    style={{
                      background:
                        "linear-gradient(to right, var(--iw-gold), transparent)",
                    }}
                  />

                  <p className="text-[var(--iw-ivory-dim)] text-base md:text-lg leading-[1.75] max-w-md font-light">
                    {c.heritage}
                  </p>

                  {c.quote && (
                    <blockquote
                      className="mt-8 font-serif-display italic text-[var(--iw-ivory)] text-2xl md:text-3xl leading-snug max-w-md"
                      style={{ fontFamily: "Cormorant Garamond, serif" }}
                    >
                      "{c.quote}"
                    </blockquote>
                  )}

                  {/* Instagram handles — minimal */}
                  <div className="mt-10 flex flex-wrap items-center gap-5">
                    {c.handles.map((h) => (
                      <a
                        key={h.handle}
                        href={h.url}
                        target="_blank"
                        rel="noreferrer"
                        className="group inline-flex items-center gap-2 text-[var(--iw-gold)] hover:text-[var(--iw-rose)] transition-colors"
                        data-testid={`client-handle-${h.handle}`}
                      >
                        <Instagram size={14} strokeWidth={1.25} />
                        <span
                          className="font-serif-display italic text-xl tracking-tight"
                          style={{ fontFamily: "Cormorant Garamond, serif" }}
                        >
                          @{h.handle}
                        </span>
                        <ArrowUpRight
                          size={14}
                          className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* CTA — minimal editorial */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-28 md:mt-40 text-center max-w-3xl mx-auto"
        >
          <div
            className="mx-auto h-px w-24 mb-10"
            style={{
              background:
                "linear-gradient(to right, transparent, var(--iw-gold), transparent)",
            }}
          />
          <h3
            className="font-serif-display text-[var(--iw-ivory)] text-4xl md:text-5xl leading-[1.02]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Loved these stories?
            <br />
            <span className="italic gold-rose-gradient">
              Let's design yours.
            </span>
          </h3>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href={`https://wa.me/917380587328?text=Hi%20Invitation%20Wala%2C%20I%27d%20love%20to%20discuss%20an%20invite.`}
              target="_blank"
              rel="noreferrer"
              className="btn-gold"
              data-testid="stories-cta-whatsapp"
            >
              DM on WhatsApp
            </a>
            <a
              href="https://www.instagram.com/invitation_wala_/"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost-gold"
              data-testid="stories-cta-ig"
            >
              <Instagram size={14} strokeWidth={1.25} /> View Archive
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
